import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useRevit } from '../../contexts/RevitContext';
import './YouTubeConnectModal.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const SEEN_KEY = 'hasSeenYTConnectPrompt';

const YouTubeConnectModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, isLoaded } = useUser();
  const { username, youtubeName } = useRevit();
  const navigate = useNavigate();
  const syncedDismiss = useRef(false);

  const markSeen = async () => {
    localStorage.setItem(SEEN_KEY, 'true');
    if (!user || user.unsafeMetadata?.[SEEN_KEY] === true) return;
    try {
      await user.update({
        unsafeMetadata: { ...user.unsafeMetadata, [SEEN_KEY]: true },
      });
    } catch {
      // localStorage fallback already set above
    }
  };

  // Dismissal is stored on the Clerk user so it follows the account to every device.
  useEffect(() => {
    if (!isLoaded || !user) return;

    const cutoff = new Date('2026-04-21T00:00:00Z');
    if (user.createdAt && new Date(user.createdAt) < cutoff) return;

    const dismissedInClerk = user.unsafeMetadata?.[SEEN_KEY] === true;
    const dismissedLocally = localStorage.getItem(SEEN_KEY) === 'true';

    if (dismissedInClerk) {
      localStorage.setItem(SEEN_KEY, 'true');
    } else if ((dismissedLocally || youtubeName) && !syncedDismiss.current) {
      syncedDismiss.current = true;
      markSeen().catch(() => {
        syncedDismiss.current = false;
      });
    }

    if (dismissedInClerk || dismissedLocally || youtubeName) {
      setIsOpen(false);
      return;
    }

    const timer = setTimeout(() => setIsOpen(true), 1200);
    return () => clearTimeout(timer);
  }, [isLoaded, user, youtubeName]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = () => {
    const uid = username;
    if (!uid) return;
    const url = `https://${SERVER_URL}/api/auth?userId=${uid}`;
    window.open(url, '_blank');
    markSeen();
    setIsOpen(false);
  };

  const handleSkip = () => {
    markSeen();
    setIsOpen(false);
    navigate('/code');
  };

  if (!isOpen) return null;

  return (
    <div className="ytc-overlay" onClick={handleSkip}>
      <div className="ytc-modal" onClick={(e) => e.stopPropagation()}>
        {/* Icon */}
        <div className="ytc-icon-wrap">
          <svg className="ytc-yt-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
        </div>

        {/* Header */}
        <h2 className="ytc-title">Welcome to Revtrack. Connect your YouTube channel to get started.</h2>
        <p className="ytc-desc">
          Link your channel below to start tracking.
        </p>

        {/* Actions */}
        <div className="ytc-actions">
          <button className="ytc-btn-connect" onClick={handleConnect}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            Connect YouTube
          </button>
          <button className="ytc-btn-skip" onClick={handleSkip}>
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};

export default YouTubeConnectModal;
